import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../utils/apiClient';

/**
 * Tablolardaki kolon tercihlerini yöneten custom hook.
 * Backend'e kaydeder, çeker ve havuzdan sadece güvenli olanları eşleştirir.
 * 
 * @param {string} pageKey - Sayfa için eşsiz anahtar (örn: 'personnel_management_list')
 * @param {Array} columnsPool - Tüm seçilebilir/sabit kolonların dizisi [{ accessor: 'id', header: 'ID' }, ...]
 * @param {Array} defaultVisibleColumns - Varsayılan olarak gösterilecek kolonların accessor listesi
 * @param {Array} fixedColumns - Kullanıcının kapatamayacağı sabit kolonların accessor listesi
 * @returns {Object} - visibleColumns, updatePreferences, resetPreferences ve loading state
 */
export default function useListColumnPreferences(pageKey, columnsPool, defaultVisibleColumns, fixedColumns) {
  const [visibleColumns, setVisibleColumns] = useState([...defaultVisibleColumns]);
  const [isLoading, setIsLoading] = useState(true);

  // Pool'daki accessor'ları güvenli bir set olarak tutalım (sistemden kaldırılanları elemek için)
  const poolAccessors = new Set(columnsPool.map(c => c.accessor));

  const fetchPreferences = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiClient(`UserPreferences/${pageKey}`);
      if (response && response.preferencesJson) {
        const parsed = JSON.parse(response.preferencesJson);
        if (Array.isArray(parsed) && parsed.length > 0) {
          // Gelen JSON'daki kolonlar havuzda hala var mı? Yoksa ignore et.
          const safeColumns = parsed.filter(col => poolAccessors.has(col));
          
          // Sabit kolonlar eksikse ekle
          const finalColumns = [...new Set([...fixedColumns, ...safeColumns])];
          setVisibleColumns(finalColumns);
        } else {
          // Boşsa veya dizi değilse varsayılana dön
          setVisibleColumns([...defaultVisibleColumns]);
        }
      }
    } catch (error) {
      console.error(`Kolon tercihleri alınırken hata (${pageKey}):`, error);
      // Hata olsa bile varsayılanları kullan
      setVisibleColumns([...defaultVisibleColumns]);
    } finally {
      setIsLoading(false);
    }
  }, [pageKey, fixedColumns, defaultVisibleColumns, poolAccessors]);

  useEffect(() => {
    fetchPreferences();
  }, []);

  const updatePreferences = async (newVisibleColumns) => {
    // Sabit kolonları zorunlu olarak ekle (güvenlik için)
    const finalColumns = [...new Set([...fixedColumns, ...newVisibleColumns])];
    
    // Sadece havuzda olanları al
    const safeFinalColumns = finalColumns.filter(col => poolAccessors.has(col));
    
    setVisibleColumns(safeFinalColumns);

    try {
      await apiClient(`UserPreferences/${pageKey}`, {
        method: 'PUT',
        body: safeFinalColumns
      });
    } catch (error) {
      console.error(`Kolon tercihleri kaydedilirken hata (${pageKey}):`, error);
    }
  };

  const resetPreferences = async () => {
    setVisibleColumns([...defaultVisibleColumns]);
    try {
      await apiClient(`UserPreferences/${pageKey}`, {
        method: 'PUT',
        body: defaultVisibleColumns
      });
    } catch (error) {
      console.error(`Kolon tercihleri sıfırlanırken hata (${pageKey}):`, error);
    }
  };

  return {
    visibleColumns,
    updatePreferences,
    resetPreferences,
    isLoading
  };
}
